import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { ModalController } from '@ionic/angular';
import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import {
  amountMask,
  cleanUpMaskValue,
  rateAmountMask,
  rateAmountSymbolRegExPattern,
  rateMask,
  ratePostfix
} from '../../../constants/maskito.constants';
import { PrimarySubscriptionPlanAccessAliasEnum } from '../../../enums/monetization';
import * as fromCalculatorActions from '../../../store/actions/calculator.actions';
import * as fromCalculatorSelectors from '../../../store/selectors/calculator.selectors';
import { SearchableSelectOption } from '../../../typings/searchable-select';
import { MonetizationService } from '../../services/monetization.service';
import { ReportModalComponent } from './report-modal/report-modal.component';

interface AffordabilityFormGroup {
  annualOwnersInsurance: FormControl<string | null>;
  annualPropertyTaxes: FormControl<string | null>;
  buyerName: FormControl<string | null>;
  downPayments: FormControl<string | null>;
  interestRate: FormControl<string | null>;
  monthlyPayments: FormControl<string | null>;
  mortgageTerm: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-affordability',
  standalone: false,
  templateUrl: './affordability.page.html'
})
export class AffordabilityPageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private modalController = inject(ModalController);
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  defaultBackHref: string | undefined;

  mortgageTermData: SearchableSelectOption[] = [
    { name: '5 years', value: '5' },
    { name: '7 years', value: '7' },
    { name: '10 years', value: '10' },
    { name: '15 years', value: '15' },
    { name: '20 years', value: '20' },
    { name: '25 years', value: '25' },
    { name: '30 years', value: '30' },
    { name: '35 years', value: '35' }
  ];

  form: FormGroup<AffordabilityFormGroup> = new FormGroup<AffordabilityFormGroup>({
    annualOwnersInsurance: new FormControl('1000', Validators.required),
    annualPropertyTaxes: new FormControl(null, Validators.required),
    buyerName: new FormControl(''),
    downPayments: new FormControl(null, Validators.required),
    interestRate: new FormControl(null, Validators.required),
    monthlyPayments: new FormControl(null, Validators.required),
    mortgageTerm: new FormControl(null, Validators.required)
  });

  readonly rateMask: MaskitoOptions = rateMask;
  readonly amountMask: MaskitoOptions = amountMask;
  readonly rateAmountMask: MaskitoOptions = rateAmountMask();
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];

    this.store.pipe(select(fromCalculatorSelectors.selectAffordabilityMortgageTerm)).subscribe((mortgageTerm) => {
      const value = mortgageTerm.toString();
      if (this.form.controls.mortgageTerm.value !== value) {
        this.form.controls.mortgageTerm.setValue(value);
      }
    });
  }

  mortgageTermChanged(event: CustomEvent) {
    this.store.dispatch(
      fromCalculatorActions.updateAffordabilityMortgageTerm({ payload: { mortgageTerm: Number(event.detail.value) } })
    );
  }

  async calculate() {
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      const formValues = this.form.getRawValue();

      this.store.dispatch(
        fromCalculatorActions.updateAffordabilityCalculatorData({
          payload: {
            annualOwnersInsurance: cleanUpMaskValue(formValues.annualOwnersInsurance, rateAmountSymbolRegExPattern),
            annualPropertyTaxes: cleanUpMaskValue(formValues.annualPropertyTaxes, rateAmountSymbolRegExPattern),
            buyerName: formValues.buyerName || null,
            downPayments: cleanUpMaskValue(formValues.downPayments, rateAmountSymbolRegExPattern),
            interestRate: cleanUpMaskValue(formValues.interestRate, ratePostfix),
            monthlyPayments: cleanUpMaskValue(formValues.monthlyPayments, rateAmountSymbolRegExPattern)
          }
        })
      );

      const modal = await this.modalController.create({
        component: ReportModalComponent
      });

      await modal.present();
    }
  }
}
